import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import Hero from './components/Hero';
import ClientLogos from './components/ClientLogos';
import About from './components/About';
import Services from './components/Services';
import WhyChooseUs from './components/WhyChooseUs';
import ComparisonTable from './components/ComparisonTable';
import TechAnalysis from './components/TechAnalysis';
import Showcase from './components/Showcase';
import ContactCTA from './components/ContactCTA';
import Footer from './components/Footer';
import Navbar from './components/Navbar';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const isZh = language === 'zh';

    const schemaData = [
      // 1. LocalBusiness
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${SITE_URL}/#business`,
        name: 'IBUS (HK) LTD',
        description: isZh
          ? '專業IT綜合服務供應商，提供結構化佈線系統、IT基礎設施規劃與全方位受管理IT服務。'
          : 'Professional IT comprehensive service provider, offering structured cabling systems, IT infrastructure planning, and comprehensive managed IT services.',
        url: SITE_URL,
        telephone: '+85235795580',
        email: 'info@ibushk.com',
        image: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/92677d645ff7770f827851cf36daa564.png',
        priceRange: '$$',
        address: {
          '@type': 'PostalAddress',
          streetAddress: isZh
            ? '皇后大道中346-348號金煌行13/F A室'
            : 'Room A, 13/F, Golden Crown Court, 346-348 Queen\'s Road Central',
          addressLocality: isZh ? '上環' : 'Sheung Wan',
          addressRegion: isZh ? '香港' : 'Hong Kong',
          addressCountry: 'HK',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 22.2858,
          longitude: 114.1494,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
        ],
        areaServed: {
          '@type': 'Place',
          name: isZh ? '香港' : 'Hong Kong',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: isZh ? 'IT 綜合服務' : 'IT Comprehensive Services',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: isZh ? '結構化佈線系統' : 'Structured Cabling System',
                description: isZh
                  ? 'Cat6/6A 及光纖網路佈線設計與施工，數據中心機房規劃與理線工程，Fluke 測試認證。'
                  : 'Cat6/6A and fiber optic network cabling design and construction, data center planning and cable management, Fluke testing and certification.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: isZh ? 'IT 基礎設施規劃' : 'IT Infrastructure Planning',
                description: isZh
                  ? '伺服器與存儲系統佈署，企業級網路架構設計，雲端與本地混合架構整合，VoIP 系統架構佈署。'
                  : 'Server and storage system deployment, enterprise network architecture design, cloud and on-premises hybrid architecture integration, VoIP system deployment.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: isZh ? '全方位受管理 IT 服務' : 'Comprehensive Managed IT Services',
                description: isZh
                  ? '7x24 小時遠端與現場技術支援，主動式網路安全監控，系統備援與災難恢復管理。'
                  : '7x24 remote and on-site technical support, proactive network security monitoring, system backup and disaster recovery management.',
              },
            },
          ],
        },
        sameAs: [],
      },

      // 2. WebSite
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'IBUS (HK) LTD',
        description: isZh
          ? '專業IT綜合服務供應商，為企業打造堅實的數位基礎。'
          : 'Professional IT comprehensive service provider, building solid digital foundations for enterprises.',
        inLanguage: isZh ? 'zh-TW' : 'en',
        publisher: { '@id': `${SITE_URL}/#business` },
      },

      // 3. WebPage
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: isZh
          ? 'IBUS (HK) LTD | IT綜合服務 結構化佈線 IT基礎設施 受管理IT服務'
          : 'IBUS (HK) LTD | IT Services Structured Cabling IT Infrastructure Managed IT Services',
        description: isZh
          ? '專業IT綜合服務供應商，提供結構化佈線系統、IT基礎設施規劃與全方位受管理IT服務。'
          : 'Professional IT comprehensive service provider, offering structured cabling systems, IT infrastructure planning, and comprehensive managed IT services.',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#business` },
        inLanguage: isZh ? 'zh-TW' : 'en',
        dateModified: '2025-07-15',
        breadcrumb: { '@id': `${SITE_URL}/#breadcrumb` },
      },

      // 4. BreadcrumbList
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${SITE_URL}/#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: isZh ? '首頁' : 'Home',
            item: SITE_URL,
          },
        ],
      },

      // 5. FAQPage
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: isZh
          ? [
              {
                '@type': 'Question',
                name: '什麼是結構化佈線系統？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '結構化佈線系統是一套標準化的電信佈線基礎設施，支援多種硬件使用及電信服務，包括 Cat6/6A 及光纖網路佈線設計與施工，並通過 Fluke 測試認證，確保網絡穩定性與可靠性。',
                },
              },
              {
                '@type': 'Question',
                name: 'IBUS (HK) LTD 提供哪些受管理IT服務？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'IBUS (HK) LTD 提供全方位受管理IT服務，包括 7x24 小時遠端與現場技術支援、主動式網路安全監控、系統備援與災難恢復管理，確保企業IT基礎設施持續穩定運行。',
                },
              },
              {
                '@type': 'Question',
                name: 'IBUS (HK) LTD 的服務範圍是哪裡？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'IBUS (HK) LTD 主要服務香港地區的企業客戶，提供結構化佈線、IT基礎設施規劃及受管理IT服務，辦公室位於香港上環皇后大道中346-348號金煌行13/F A室。',
                },
              },
              {
                '@type': 'Question',
                name: '如何聯絡 IBUS (HK) LTD？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以致電 (852) 3579 5580 或發送電郵至 info@ibushk.com 聯絡我們。辦公時間為週一至週五 09:00–18:00。',
                },
              },
            ]
          : [
              {
                '@type': 'Question',
                name: 'What is a structured cabling system?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'A structured cabling system is a standardized telecommunications cabling infrastructure that supports multiple hardware uses and telecommunications services, including Cat6/6A and fiber optic network cabling design and construction, certified by Fluke testing to ensure network stability and reliability.',
                },
              },
              {
                '@type': 'Question',
                name: 'What managed IT services does IBUS (HK) LTD provide?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'IBUS (HK) LTD provides comprehensive managed IT services including 7x24 remote and on-site technical support, proactive network security monitoring, and system backup and disaster recovery management to ensure continuous stable operation of enterprise IT infrastructure.',
                },
              },
              {
                '@type': 'Question',
                name: 'Where does IBUS (HK) LTD serve?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'IBUS (HK) LTD primarily serves enterprise clients in Hong Kong, providing structured cabling, IT infrastructure planning, and managed IT services. Our office is located at Room A, 13/F, Golden Crown Court, 346-348 Queen\'s Road Central, Sheung Wan, Hong Kong.',
                },
              },
              {
                '@type': 'Question',
                name: 'How to contact IBUS (HK) LTD?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'You can reach us by calling (852) 3579 5580 or emailing info@ibushk.com. Office hours are Monday to Friday, 09:00–18:00.',
                },
              },
            ],
      },
    ];

    const existingScripts = document.querySelectorAll('script[data-schema="home"]');
    existingScripts.forEach((s) => s.remove());

    schemaData.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'home');
      script.setAttribute('data-schema-index', String(i));
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="home"]').forEach((s) => s.remove());
    };
  }, [language]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />
      <Hero />
      <ClientLogos />
      <About />
      <Services />
      <WhyChooseUs />
      <ComparisonTable />
      <TechAnalysis />
      <Showcase />
      <ContactCTA />
      <Footer />

      {/* 回到頂部按鈕 */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-[99999] w-11 h-11 flex items-center justify-center rounded-full bg-[#00A3FF] text-white shadow-lg transition-all duration-300 cursor-pointer hover:bg-[#0088dd] hover:shadow-xl hover:-translate-y-1 ${
          showScrollTop ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-label="回到頂部"
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>
    </div>
  );
}
